export const SIGNUP_FAILURE_CODES = [
  "validation",
  "captcha",
  "email_verification",
  "duplicate",
  "code_exchange",
  "unknown",
] as const

export type SignupFailureCode = (typeof SIGNUP_FAILURE_CODES)[number]

const EMAIL_VERIFICATION_MESSAGES = new Set([
  "This email address could not be verified. Please use a valid, deliverable email.",
  "We couldn't verify this email. Please use a different address.",
  "We couldn't verify this email right now. Please try again later.",
  "Email verification is temporarily unavailable. Please try again later.",
  "Too many verification requests. Please try again in a few minutes.",
  "Too many attempts from your connection. Please try again in an hour.",
  "Please wait before requesting another email.",
  "You've reached the limit.",
  "Too many sign-up attempts. Please wait a few minutes and try again.",
])

const VALIDATION_MESSAGES = new Set([
  "We don't accept temporary or disposable email addresses. Please use a personal or work email.",
  "Disposable or temporary email addresses are not allowed.",
  "This email domain is not allowed.",
  "That password is too easy to guess. Please choose a stronger password.",
  "Email is required.",
])

const DUPLICATE_MESSAGES = new Set([
  "An account with this email already exists. Try signing in instead.",
])

/** Closed telemetry code only. The input message is never returned. */
export function signupFailureCode(input: {
  code?: string | null
  message?: string | null
  stage?: "code_exchange" | "session_check"
}): SignupFailureCode {
  const code = input.code?.toLowerCase() ?? ""
  const message = input.message ?? ""
  const normalized = message.toLowerCase()

  if (code === "captcha_failed" || normalized.includes("captcha")) return "captcha"
  if (
    code === "user_already_exists" ||
    code === "email_exists" ||
    DUPLICATE_MESSAGES.has(message) ||
    normalized.includes("already registered") ||
    normalized.includes("already exists")
  ) {
    return "duplicate"
  }
  if (
    code === "weak_password" ||
    code === "validation_failed" ||
    VALIDATION_MESSAGES.has(message)
  ) {
    return "validation"
  }
  if (
    EMAIL_VERIFICATION_MESSAGES.has(message) ||
    code === "email_address_invalid" ||
    code === "hook_timeout" ||
    code.startsWith("over_")
  ) {
    return "email_verification"
  }
  if (input.stage === "code_exchange") return "code_exchange"
  if (message === "An account using this sign-in method was previously closed. To prevent abuse, new accounts cannot be created with the same sign-in method. If you believe this is an error, contact support." || message === "We couldn't create your account right now. Please try again in a few minutes.") return "unknown"
  return "unknown"
}

/** Client domain block never reaches submit. Return a code only for that message. */
export function blockedDomainSignupCode(message: string | undefined): SignupFailureCode | null {
  if (message !== "This email domain is not allowed.") return null
  return signupFailureCode({ message })
}
